import { Stack } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import ScrollBox from '@/components/scroll-box';
import SearchInput from '@/components/search-input';
import UserSettingsCard from './card';
import useUserSettings from '@/hooks/use-user-settings.hook';
import CardOpt from './card-opt';
import CardDetail from './card-detail';

interface Props {
  max: number;
}

const DatasourceCard: FC<Props> = ({ max }) => {
  const { datasources, isLoading, threadSettings, toggleDatasource } = useUserSettings();

  const [keyword, setKeyword] = useState('');

  const filteredDatasources = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();

    return datasources.filter(({ name, description, isPublicInternet }) => {
      // there is a separate switch for toggling public internet search datasources
      if (isPublicInternet) {
        return false;
      }

      const keywordMatch =
        String(name).toLowerCase().includes(safeKeyword) ||
        String(description).toLowerCase().includes(safeKeyword);

      return keywordMatch;
    });
  }, [datasources, keyword]);

  const handleToggleDatasource = (id: string, enabled: boolean) => {
    toggleDatasource(id, !enabled);
  };

  const isEnabled = useCallback(
    (id: string) => {
      return threadSettings.datasources.some(({ id: dsId, enabled }) => dsId === id && enabled);
    },
    [threadSettings]
  );

  const selectedDatasourceId = useMemo(() => {
    return filteredDatasources.find(({ id }) => isEnabled(id))?.id;
  }, [filteredDatasources, isEnabled]);

  const hasDatasources = !!filteredDatasources.length;
  const noResults = !isLoading && !!keyword.length && !hasDatasources;
  const emptyMessage = noResults ? 'No matching data sources found' : '';
  const showEmptyMessage = !isLoading && !hasDatasources && !!emptyMessage;

  return (
    <UserSettingsCard title="Enterprise  Data Sources" loading={isLoading}>
      <CardDetail>Choose the enterprise data sources to search when sending a prompt.</CardDetail>
      <Stack direction="row" justifyContent="space-between" alignItems="center" className="toolbar">
        <SearchInput
          value={keyword}
          onChange={setKeyword}
          placeholder="Search by name"
          disableHideInput
        />
      </Stack>

      <ScrollBox
        max={max}
        emptyMessage={showEmptyMessage ? emptyMessage : ''}
        selected={selectedDatasourceId}
      >
        {filteredDatasources.map((datasource) => {
          const { id, name } = datasource;
          const enabled = isEnabled(id);

          return (
            <CardOpt
              key={id}
              id={id}
              name={name}
              selected={enabled}
              onChange={(id) => handleToggleDatasource(id, enabled)}
            />
          );
        })}
      </ScrollBox>
    </UserSettingsCard>
  );
};

export default DatasourceCard;
